<template>
  <div class="gf_corner">
    <perfect-scrollbar class="gf_corner__scroll-body">
      <div class="corner-content">
        <div class="gf_editor-corner">
          <Pack
            v-for="(item, index) in currentPresetOptions"
            :key="index"
            :item="item"
            :index-item="index"
            :selected-pack="selectedPack"
            :is-ready-to-save="isReadyToSave"
            :invalid-pack="invalidPack"
            :type-pack="'corner'"
            @validateItemName="validateItemName"
            @onChangePack="selectedPack = $event"
            @onDelete="onDeleteCorner"
            @onDuplicate="onDuplicateCorner">
            <template #settingSlot>
              <EditCornerItem :corner-item="item" :index-item="index" @onChangeCorner="onChangeCorner" />
            </template>
          </Pack>
        </div>
        <div v-if="newElementValid" class="gf_corner__btn" @click="onAddNewStyle()">
          <i class="icon icon-add gf_corner__btn-icon" />
          <div class="gf_corner__btn-action paragraph-12-regular">Add New Corner</div>
        </div>
      </div>

      <GuideCustomizeStyle
        v-if="!obGlobalStyle.tooltipCornerStyle"
        title="Setup Corner"
        guide="tooltipCornerStyle"
        link="https://help.gempages.net/articles/customize-global-style#table-content-3"
        content="You can choose to use corner style for elements you need. You can find the option in tab Design of element." />
    </perfect-scrollbar>
  </div>
</template>

<script lang="ts">
import Pack from './/Pack.vue';
import EditCornerItem from './/CornerItem.vue';
import { defineComponent } from 'vue';
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex';
import { mutations } from '@modules/editor-sidebar';
import type { PresetOptionCorner } from '@/types/global-style';
import { presetOptionTypes } from '../../constants/styleConstant';
import { cloneDeep } from '@/utils/common';
import GuideCustomizeStyle from '../guide/GuideCustomizeStyle.vue';
import HELPER from '@/app/utils/HELPER';

interface DataInterface {
  plusIcon: string;
  selectedPack: any;
  isReadyToSave: boolean;
  invalidPack: any;
}

export default defineComponent({
  name: 'Corner',
  components: {
    Pack,
    EditCornerItem,
    GuideCustomizeStyle,
  },
  data: (): DataInterface => ({
    plusIcon: 'static/icons/plus.svg',
    isReadyToSave: true,
    selectedPack: -1,
    invalidPack: -1,
  }),
  computed: {
    ...mapState('sidebar', {
      currentPreset: (state) => (state as any).currentPreset,
      styleIdTargetGlobalStyle: (state) => (state as any).styleIdTargetGlobalStyle,
    }),
    ...mapGetters('sidebar', {
      currentPresetOptions: 'getCurrentPresetOptions',
    }),
    ...mapState('app', {
      shopMeta: 'shopMeta',
    }),
    obGlobalStyle() {
      return this.shopMeta.onboardingGlobalStyle;
    },
    elementNames() {
      return this.currentPresetOptions
        .filter((element: { destroy: any }) => !element.destroy)
        .map((element: { name: any }) => element.name);
    },
    newElementValid() {
      return this.elementNames.length < 10;
    },
  },
  watch: {
    styleIdTargetGlobalStyle(val) {
      if (val) {
        const indexItem = this.currentPresetOptions.findIndex((item: any) => item.id == val);
        this.selectedPack = indexItem;
      }
    },
    isReadyToSave(newVal) {
      this.$emit('isReadyToSave', newVal);
    },
  },
  mounted() {
    this.setPresetTypeId(presetOptionTypes.corner);
    this.makeCurrentPresetOptions();
    this.setStyleIdTargetGS(parseInt(this.styleIdTargetGlobalStyle));
    const indexItem = this.currentPresetOptions.findIndex((item: any) => item.id == this.styleIdTargetGlobalStyle);
    this.selectedPack = indexItem;
  },
  methods: {
    ...mapMutations('sidebar', {
      setPresetTypeId: mutations.SET_PRESET_TYPE_ID,
      setCurrentPresetOptions: mutations.SET_CURRENT_PRESET_OPTIONS,
      setStyleIdTargetGS: mutations.SET_STYLE_ID_TARGET_GLOBAL_STYLE,
    }),
    ...mapActions('app', {
      updateShopMeta: 'updateShopMeta',
    }),
    makeCurrentPresetOptions(): void {
      this.setCurrentPresetOptions(cloneDeep(this.currentPreset.presetOptions.corners));
    },
    validateItemName(data: any): void {
      this.isReadyToSave = data.isAvailable;
      this.invalidPack = data.invalidPack;
    },
    cancel() {
      this.$emit('cancel');
    },
    onAddNewStyle() {
      if (!this.isReadyToSave) return;
      if (!this.newElementValid) return;

      const elementNames = this.currentPresetOptions.map((element: { name: any }) => element.name);
      let i = 1;
      while (elementNames.includes(this.generateCreateNewName(i))) i++;

      this.currentPresetOptions.push({
        id: `gpid_${HELPER.MakeId()}`,
        name: this.generateCreateNewName(i),
        type: 'corner',
        isDefault: false,
        value: {
          mode: 'all',
          value: '0px 0px 0px 0px',
        },
      });
    },
    generateDuplicateName(name: string, index: number): string {
      return `${name.substring(0, 20)} ${index > 0 ? index : ''}`.trim();
    },
    generateCreateNewName(index: number): string {
      return `Corner ${index > 0 ? index : ''}`.trim();
    },
    onDuplicateCorner(index: any) {
      if (!this.isReadyToSave) return;
      if (!this.newElementValid) return;

      const iDuplicate = this.currentPresetOptions[index];

      const elementNames = this.currentPresetOptions.map((element: { name: any }) => element.name);
      let i = 1;
      while (elementNames.includes(this.generateDuplicateName(iDuplicate.name, i))) i++;

      const duplicateItem: PresetOptionCorner = {
        id: `gpid_${HELPER.MakeId()}`,
        isDefault: false,
        name: this.generateDuplicateName(iDuplicate.name, i),
        type: 'corner',
        value: JSON.parse(JSON.stringify(iDuplicate.value)),
      };
      this.currentPresetOptions.push(duplicateItem);
    },
    onDeleteCorner(index: any) {
      if (!this.isReadyToSave) return;

      if (this.currentPresetOptions[index].id) {
        this.currentPresetOptions[index].destroy = true;
        this.currentPresetOptions[index].name = undefined;
      } else {
        this.currentPresetOptions.splice(index, 1);
      }
    },
    onChangeCorner(corner: any) {
      const indexItem = corner.indexItem;
      delete corner.indexItem;
      (this.currentPresetOptions as any)[indexItem] = corner;
    },
  } as any,
});
</script>

<style scoped lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.gf_corner {
  height: calc(100% - 126px);

  &__scroll-body {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
}

.corner-content {
  flex: 1;
}

.gf_editor-corner {
  border-radius: $radius;
  background: map-get($hybrid-shade-dark, '400');
  margin: 16px 0;
  padding: 0 12px;

  :deep(.wrap-row) {
    padding: 4px;
    min-height: 32px;

    .center-col {
      flex: 1;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding-left: 0;

      .lbl-input {
        font-size: 12px;
        background-color: transparent;
        border: 0;
        color: $highlight2;
        border-radius: $radius;
        padding: 4px !important;
        border: 1px solid transparent;
        cursor: text;

        &[data-input-focus='true'] {
          margin-left: 0 !important;
        }

        &:focus {
          border: 1px solid map-get($primary-blue, '275');
        }
      }
    }

    .right-col {
      .wrap-absolute-action {
        .item-action {
          width: 24px;
          height: 24px;
          padding: 4px;
          margin-right: 4px;
          cursor: pointer;
        }
      }

      .link-col-action {
        width: 24px;
        height: 24px;
        padding: 4px;
      }
    }
  }
}

:deep(.wrap-panel-settings) {
  border: 1px solid map-get($hybrid-shade-dark, '200');
  border-top: 0;
  border-radius: 0 0 3px 3px;
}

.gf_corner__btn {
  @include flex-align-justify-center;
  border: 1px dashed $highlight1;
  border-radius: $radius;
  padding: 8px;
  margin: 0 12px;
  cursor: pointer;

  &-icon {
    margin-top: -1px;
    width: 11px;
    height: 11px;
    margin-right: 8px;
  }

  &-action {
    text-align: center;
    font-family: 'Inter';
    color: map-get($text-dark-mode, 'high-contrast');
  }
}
</style>
